import Vue from "vue";
import Router from "vue-router";
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";
import VueAnalytics from "vue-analytics";
import Home from "./views/Home.vue";
import Signup from "./views/Signup.vue";
import Login from "./views/Login.vue";
import Account from "./views/Account.vue";
import Password from "./views/Password.vue";
import PasswordReset from "./views/PasswordReset.vue";
import Reset from "./views/Reset.vue";
import Artwork from "./views/Artwork.vue";
import Gallery from "./views/Gallery.vue";
import About from "./views/About/About.vue";
import Artist from "./views/About/Artist.vue";
import Introduction from "./views/About/Introduction";
import RegistrationProcess from "./views/About/RegistrationProcess.vue";
import Upload from "./views/Upload.vue";
import Profile from "./views/Profile.vue";
import Invites from "./views/Invites.vue";
import Invite from "./views/Invite.vue";
import ApplicationUpload from "./views/ApplicationUpload.vue";
import ApplicationPublished from "./views/ApplicationPublished.vue";
import Application from "./views/Application.vue";
import ApplicationComplete from "./views/ApplicationComplete.vue";
import { requireAuth } from "./auth";

Vue.use(Router);

const router = new Router({
  mode: "history",
  routes: [
    {
      path: "/",
      name: "home",
      component: Home
    },
    {
      path: "/signup/",
      name: "signup",
      component: Signup
    },
    {
      path: "/login/",
      name: "login",
      component: Login
    },
    {
      path: "/accounts/",
      name: "account",
      component: Account
    },
    {
      path: "/accounts/password/",
      name: "password",
      component: Password
    },
    {
      path: "/accounts/password/reset/",
      name: "passwordReset",
      component: PasswordReset
    },
    {
      path: "/accounts/reset/:uidb64/:token/",
      name: "Reset",
      component: Reset
    },
    {
      path: "/artwork/:artwork/",
      name: "artwork",
      component: Artwork
    },
    {
      path: "/gallery/",
      redirect: "gallery/all/"
    },
    {
      path: "/gallery/:category/",
      name: "gallery",
      component: Gallery
    },
    {
      path: "/upload/",
      name: "upload",
      component: Upload,
      props: true,
      beforeEnter: requireAuth
    },
    {
      path: "/upload/:artworkId/",
      name: "upload-with-artwork",
      component: Upload,
      props: true,
      beforeEnter: requireAuth
    },
    {
      path: "/about/",
      name: "about",
      component: About,
      children: [
        {
          path: "artists/",
          component: Artist,
          children: [
            {
              path: "registration-process/",
              component: RegistrationProcess
            },
            {
              path: "benefits/",
              component: Introduction,
              props: { items: require("./assets/benefits.json") }
            },
            {
              path: "*",
              redirect: "registration-process/"
            }
          ]
        },
        {
          path: "brands-and-agencies/",
          component: Introduction,
          props: { items: require("./assets/brands-and-agencies.json") }
        },
        {
          path: "collectors/",
          component: Introduction,
          props: { items: require("./assets/collectors.json") }
        },
        {
          path: "axoo-agency/",
          component: Introduction,
          props: { items: require("./assets/axoo-agency.json") }
        },
        {
          path: "contact/",
          component: Introduction,
          props: { items: require("./assets/contact.json") }
        }
      ]
    },
    {
      path: "/goodluck/",
      name: "invites",
      component: Invites
    },
    {
      path: "/goodluck/:id/",
      name: "invite",
      component: Invite,
      props: true
    },
    {
      path: "/goodluck/:inviteId/upload/",
      name: "application-upload",
      component: ApplicationUpload,
      props: true,
      beforeEnter: requireAuth
    },
    {
      path: "/goodluck/:inviteId/upload/:artworkId/",
      name: "application-upload-with-artwork",
      component: ApplicationUpload,
      props: true,
      beforeEnter: requireAuth
    },
    {
      path: "/goodluck/:inviteId/published/:artworkId/",
      name: "application-published-with-artwork",
      component: ApplicationPublished,
      props: true,
      beforeEnter: requireAuth
    },
    {
      path: "/goodluck/:inviteId/application/:artworkId/",
      name: "application-with-artwork",
      component: Application,
      props: true,
      beforeEnter: requireAuth
    },
    {
      path: "/goodluck/:inviteId/complete/:artworkId/",
      name: "application-complete-with-artwork",
      component: ApplicationComplete,
      props: true,
      beforeEnter: requireAuth
    },
    {
      path: "/:slug/",
      name: "profile",
      component: Profile
    }
  ],
  scrollBehavior() {
    return { x: 0, y: 0 };
  }
});

Sentry.init({
  Vue,
  dsn:
    "https://be919af37a8547dfaa7396378dad1f82@o164964.ingest.sentry.io/4504436393050112",
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracePropagationTargets: ["localhost", "pixpills.com", /^\//]
    })
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0
});

router.afterEach(to => {
  window.fbq("trackCustom", "VirtualPageView", {
    url: `${location.origin}${to.fullPath}`
  });

  document.body.classList.remove("loading-open");

  if (to.name === "artwork" || to.name === "profile") {
    return;
  }

  document.body.classList.remove("modal-open");
});

Vue.use(VueAnalytics, {
  id: "UA-133428638-1",
  router
});

export default router;
